import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Permission, Principal } from '@dayon/rbac';
import { can } from '@dayon/rbac';
import { api, ApiRequestError } from './api.ts';

export interface Session {
  id: string;
  displayName: string;
  email: string;
  role: string;
  isSuper: boolean;
  brands: { id: string; name: string }[];
  permissions: string[];
  mfa: boolean;
  expiresAt: string;
  idleTimeoutMinutes: number;
}

export interface Brand {
  id: string;
  name: string;
  ref: string;
}

interface SessionApi {
  session: Session | null;
  /**
   * Every brand this admin may see. NOT `session.brands`, which is the scope
   * RESTRICTION — an empty restriction means "all brands", so reading the
   * selector from it showed an unscoped Super Admin no brands at all.
   */
  brands: Brand[];
  loading: boolean;
  principal: Principal | null;
  allowed: (permission: Permission) => boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * End EVERY session on this account, not just this browser's.
   *
   * A separate call from `signOut` because it is a different act: the server
   * bumps the account's token_version, so every other browser and device the
   * admin is signed in on stops being able to make a request at all. It ends
   * this session too, which is why it clears the session here as well.
   */
  signOutEverywhere: () => Promise<void>;
  /** Global brand scope. "Not a per-widget filter — it persists across modules." */
  brandId: string | null;
  setBrandId: (id: string | null) => void;
}

const Ctx = createContext<SessionApi | null>(null);

export function useSession(): SessionApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>.');
  return ctx;
}

const BRAND_KEY = 'dayon-back-office-brand';

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [brandId, setBrandIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(BRAND_KEY);
    } catch {
      return null;
    }
  });

  const refresh = useCallback(async () => {
    try {
      setSession(await api.get<Session>('/api/auth/session'));
    } catch (error) {
      // A 401 here is the normal signed-out state, not a failure worth a dialog.
      if (!(error instanceof ApiRequestError) || error.status !== 401) throw error;
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    api
      .get<{ brands: Brand[] }>('/api/brands')
      .then((d) => !cancelled && setBrands(d.brands))
      .catch(() => !cancelled && setBrands([]));
    return () => {
      cancelled = true;
    };
  }, [session]);

  const signOut = useCallback(async () => {
    await api.post('/api/auth/sign-out');
    setSession(null);
  }, []);

  /**
   * The account-wide sign-out.
   *
   * Clearing the session state — rather than reloading — is what lands the user
   * on the sign-in screen without a dead console in between: <Routed> swaps
   * <Shell> for <SignIn> in the same commit, so every module unmounts and its
   * effects are torn down before they can fire another request the bumped
   * token_version would refuse. A reload would also work, and would throw away
   * the confirmation toast the user has not read yet.
   */
  const signOutEverywhere = useCallback(async () => {
    await api.post('/api/auth/sign-out-everywhere');
    setSession(null);
  }, []);

  const setBrandId = useCallback((id: string | null) => {
    setBrandIdState(id);
    try {
      if (id) localStorage.setItem(BRAND_KEY, id);
      else localStorage.removeItem(BRAND_KEY);
    } catch {
      /* a remembered filter is not worth breaking the page over */
    }
  }, []);

  /**
   * A REMEMBERED BRAND THAT HAS LEFT THE READER'S SCOPE IS FORGOTTEN, once the session says what
   * the scope is. The header's brand is remembered in this browser between visits; a reader whose
   * scope was narrowed since came back to a brand they may no longer read, and every screen that
   * follows the header sent it — Player funds, Identity checks, Risk and Support's ticket queue
   * each answered 403 — with nothing in the console that would ever clear it.
   *
   * The scope is `session.brands`: the brands the reader is limited to, or none for a reader of
   * every brand, for whom any brand is in scope. Out of scope, the brand is handed to no screen
   * from the first render the session is in — screens then ask for the reader's own scope — and
   * forgotten here and in the browser.
   */
  const outOfScope =
    session !== null &&
    brandId !== null &&
    session.brands.length > 0 &&
    !session.brands.some((b) => b.id === brandId);
  useEffect(() => {
    if (outOfScope) setBrandId(null);
  }, [outOfScope, setBrandId]);

  /**
   * Idle timeout — the design states 20 minutes for the back office.
   * A console left open on an unattended desk is a real exposure in an
   * operation that can move money.
   */
  const lastActivity = useRef(0);
  useEffect(() => {
    if (!session) return;
    // Seeded here rather than in useRef(Date.now()), which would call an impure
    // function on every render to keep only the first result.
    lastActivity.current = Date.now();
    const bump = () => (lastActivity.current = Date.now());
    const events = ['pointerdown', 'keydown', 'scroll', 'focus'] as const;
    for (const e of events) globalThis.addEventListener(e, bump, { passive: true });

    const timer = setInterval(() => {
      const idleMs = Date.now() - lastActivity.current;
      if (idleMs > session.idleTimeoutMinutes * 60_000) {
        void signOut();
      }
    }, 30_000);

    return () => {
      for (const e of events) globalThis.removeEventListener(e, bump);
      clearInterval(timer);
    };
  }, [session, signOut]);

  const principal = useMemo<Principal | null>(
    () =>
      session
        ? {
            id: session.id,
            role: session.role,
            isSuper: session.isSuper,
            permissions: session.permissions,
            brandIds: session.brands.map((b) => b.id),
            mfa: session.mfa,
          }
        : null,
    [session],
  );

  const value = useMemo<SessionApi>(
    () => ({
      session,
      // Derived, not stored: signed out means no brands, and an effect that
      // only clears state is state that should not have been separate.
      brands: session ? brands : [],
      loading,
      principal,
      allowed: (p) => (principal ? can(principal, p) : false),
      refresh,
      signOut,
      signOutEverywhere,
      brandId: outOfScope ? null : brandId,
      setBrandId,
    }),
    [
      session,
      brands,
      loading,
      principal,
      refresh,
      signOut,
      signOutEverywhere,
      brandId,
      outOfScope,
      setBrandId,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
